import { leadsToPlanPicker, offersCheaperPlan, type CancellationReason } from "../../../shared/cancellation_reasons.js";
import type { BillingInterval, ChangePreview, PaymentMethod, Plan } from "../../../shared/types.js";

/**
 * A cheaper way to stay instead of cancelling over price (PIT-452): the Basic plan in place of
 * Premium, scheduled to the end of the paid period as any downgrade, and, when the subscription may
 * switch to it, the current plan's annual in 12x on Stone (the first year less the unused credit).
 * Offered on the confirm screen through the change flow that already exists; no discount (that is
 * PIT-455). The annual plan charged at once came out on 16/10: for someone who just said it is too
 * expensive, a year paid in one go (R$ 263 for Basic) hardly converts.
 */
export interface CheaperPlanTarget {
  plan: Plan;
  interval: BillingInterval;
  /** `stone` for the annual 12x; absent for the Basic monthly (priced on the current method). */
  paymentMethod?: PaymentMethod;
}

export interface CheaperPlanCurrent {
  plan: Plan;
  interval: BillingInterval;
  paymentMethod: PaymentMethod;
  /** A change or cancellation is already scheduled: changes go through support then. */
  hasScheduledChange: boolean;
}

/**
 * What to price for this subscription: Premium monthly gets the Basic monthly; with the 12x on offer
 * (`stoneChange`, the session's `stone_change`), each monthly plan also gets its own annual in 12x.
 * Annual has nothing cheaper per month; Stone changes go through support
 * (`stone_change_not_supported`); a scheduled change locks the page.
 */
export function cheaperPlanTargets(
  current: CheaperPlanCurrent | null,
  opts: { stoneChange: boolean } = { stoneChange: false },
): CheaperPlanTarget[] {
  if (!current || current.hasScheduledChange) return [];
  if (current.paymentMethod === "stone" || current.interval !== "monthly") return [];
  const annual12x: CheaperPlanTarget[] = opts.stoneChange
    ? [{ plan: current.plan, interval: "annual", paymentMethod: "stone" }]
    : [];
  return current.plan === "premium" ? [{ plan: "basic", interval: "monthly" }, ...annual12x] : annual12x;
}

/**
 * The plans "Quero trocar de plano" offers (PIT-546 f): every plan and interval other than the
 * current one, the other plan on the same interval first (the one picked to start with). Annual to
 * monthly by PIX is refused by the change flow (`blocked_annual_to_monthly_pix`), so not offered;
 * Stone and a scheduled change go through support, as for the cheaper plan.
 */
export function planChangeTargets(current: CheaperPlanCurrent | null): CheaperPlanTarget[] {
  if (!current || current.hasScheduledChange || current.paymentMethod === "stone") return [];
  const otherPlan: Plan = current.plan === "premium" ? "basic" : "premium";
  const otherInterval: BillingInterval = current.interval === "monthly" ? "annual" : "monthly";
  const targets: CheaperPlanTarget[] = [
    { plan: otherPlan, interval: current.interval },
    { plan: current.plan, interval: otherInterval },
    { plan: otherPlan, interval: otherInterval },
  ];
  if (current.paymentMethod === "openpix" && current.interval === "annual") {
    return targets.filter((target) => target.interval === "annual");
  }
  return targets;
}

/**
 * Where "Quero trocar de plano" leads (PIT-546 f). Someone who picks it wants to stay, so it never
 * ends in the plain cancel confirmation: the plans to switch to; for a subscription that is over,
 * the plans to come back on, picked in the plan picker as a reactivation; and where the page cannot
 * make the change, support on WhatsApp, which makes it. "Continuar cancelamento" stays one click
 * away on each.
 */
export type PlanChangePath =
  | { kind: "self_serve"; targets: CheaperPlanTarget[] }
  | { kind: "reactivate"; targets: CheaperPlanTarget[] }
  | { kind: "support" };

/**
 * `current` is the subscription as it stands, over or not, and `status` its row's status. The page
 * cannot make the change with a change already scheduled (it locks the plan picker, over or not),
 * while a switch to the 12x waits for Stone (`stone_checkout_in_progress`), or on Stone: the change
 * routes refuse any Stone row that is not `expired` (`stone_change_not_supported`), also one the page
 * already shows as over because Zenpost stopped counting it as paying. An `expired` Stone row has no
 * agreement left and buys again like anyone.
 */
export function planChangePath(
  current: CheaperPlanCurrent | null,
  opts: { subscriptionOver: boolean; status: string | null | undefined; stoneChangePending: boolean },
): PlanChangePath {
  if (!current || current.hasScheduledChange || opts.stoneChangePending) return { kind: "support" };
  if (current.paymentMethod === "stone" && opts.status !== "expired") return { kind: "support" };
  if (opts.subscriptionOver) return { kind: "reactivate", targets: reactivationTargets(current, opts.status) };
  const targets = planChangeTargets(current);
  return targets.length > 0 ? { kind: "self_serve", targets } : { kind: "support" };
}

/**
 * Every plan and interval, in the order of the switch (the other plan on the same interval first).
 * The plan it was on last only when the row is `expired` or `pending_payment`: the change flow
 * reactivates those with a charge today, and answers any other row asking for the same plan with
 * "No change requested", which the page shows as nothing at all. Annual to monthly by PIX is not
 * left out: its 30-day limit counts to the end of a period, and that one is already over.
 */
function reactivationTargets(
  last: Pick<CheaperPlanCurrent, "plan" | "interval">,
  status: string | null | undefined,
): CheaperPlanTarget[] {
  const otherPlan: Plan = last.plan === "premium" ? "basic" : "premium";
  const otherInterval: BillingInterval = last.interval === "monthly" ? "annual" : "monthly";
  const targets: CheaperPlanTarget[] = [
    { plan: otherPlan, interval: last.interval },
    { plan: last.plan, interval: otherInterval },
    { plan: otherPlan, interval: otherInterval },
  ];
  return status === "expired" || status === "pending_payment"
    ? [...targets, { plan: last.plan, interval: last.interval }]
    : targets;
}

/**
 * What the reason step offers next to "Continuar cancelamento" (PIT-546 f): the plans for
 * "Quero trocar de plano", or support where the page cannot change the plan; any other reason goes
 * on with the plain "Continuar". `change_plan` never gets "continue".
 */
export function reasonStepAction(
  reason: CancellationReason | null,
  path: PlanChangePath,
): "continue" | "plan_picker" | "support" {
  if (!leadsToPlanPicker(reason)) return "continue";
  return path.kind === "support" || path.targets.length === 0 ? "support" : "plan_picker";
}

/** Whether a priced option is the annual 12x (the only annual the card offers). */
export function isInstallmentOffer(preview: Pick<ChangePreview, "targetInterval">): boolean {
  return preview.targetInterval === "annual";
}

/** The change decisions an offer may lead to: the scheduled Basic, the annual 12x charged today. */
const OFFERABLE_DECISIONS = new Set(["schedule_downgrade_plan", "immediate_upgrade"]);

function monthlyCents(cents: number, interval: BillingInterval): number {
  return interval === "annual" ? cents / 12 : cents;
}

/**
 * Whether the priced change is one to offer: a decision the change flow carries out as it stands,
 * not blocked to a later date, and cheaper per month than what the customer pays now (a legacy price
 * can be lower than the list, and then the list is no offer).
 */
export function isCheaperPlanOffer(preview: ChangePreview | null | undefined): preview is ChangePreview {
  if (!preview || !OFFERABLE_DECISIONS.has(preview.decision) || preview.availableFrom) return false;
  return (
    monthlyCents(preview.targetPeriodPriceCents, preview.targetInterval) <
    monthlyCents(preview.currentPeriodPriceCents, preview.currentInterval)
  );
}

/** The card shows for the reason it answers, with at least one priced option. */
export function showsCheaperPlanOffer(args: {
  reason: CancellationReason | null;
  offers: readonly unknown[];
}): boolean {
  return offersCheaperPlan(args.reason) && args.offers.length > 0;
}
